
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Application } from "@/types/application";
import { GlassCard, GlassCardContent, GlassCardHeader } from "@/components/ui/glass-card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ProfileAssignment } from "@/components/profiles/ProfileAssignment";
import { useForm } from "react-hook-form";

import { formatAppSize } from "@/utils/appSize";
import { getAppSourceMeta } from "@/utils/appSource";

import { toast } from "sonner";
import { ArrowRight, Settings, UserPlus, Tags, Eye, Trash2 } from "lucide-react";
import CustomerTag from "@/components/CustomerTag";
import { getAllCustomers } from "@/utils/customerLookup";
import { ConfigureApplicationDialog } from "./ConfigureApplicationDialog";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { TagAssignmentDialog } from "@/components/tags/TagAssignmentDialog";
import { TagsDisplay } from "@/components/tags/TagsDisplay";
import { PlatformIcons } from "@/components/profiles/PlatformIcons";
import { Platform } from "@/types/profile";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface ApplicationCardProps {
  application: Application;
  onUpdate?: (app: Application) => void;
  onRemove?: (appId: string) => void;
}

export const ApplicationCard = ({ application, onUpdate, onRemove }: ApplicationCardProps) => {
  const navigate = useNavigate();
  const [isCustomerDialogOpen, setIsCustomerDialogOpen] = useState(false);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [isTagAssignmentOpen, setIsTagAssignmentOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>(application.customers || []);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  
  const form = useForm({
    defaultValues: {
      customers: application.customers || []
    }
  });

  const handleUpdateCustomers = () => {
    if (onUpdate && selectedCustomers.length > 0) {
      const updatedApp = {
        ...application,
        customers: selectedCustomers
      };
      onUpdate(updatedApp);
      setIsCustomerDialogOpen(false);
      toast.success("Application assignments updated");
    } else {
      toast.error("Please select at least one customer");
    }
  };

  const handleSaveConfiguration = (updatedApp: Application) => {
    if (onUpdate) {
      onUpdate(updatedApp);
    }
  };

  // Get customer names for display
  const customerNames = selectedCustomers
    .map(id => getAllCustomers().find(c => c.id === id)?.name || "")
    .filter(name => name !== "");

  return (
    <GlassCard variant="default" className="overflow-hidden">
      <GlassCardHeader className="p-4 pb-0 flex flex-row items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-12 h-12 rounded overflow-hidden">
            <img src={application.icon} alt={application.name} className="w-full h-full object-cover" />
          </div>
          <div>
            <Link 
              to={`/applications/${application.id}`}
              className="text-lg font-semibold hover:text-primary transition-colors block"
            >
              {application.name}
            </Link>
            <p className="text-sm text-muted-foreground">{application.bundleId}</p>
          </div>
        </div>
        <ActionsDropdown
          actions={[
            {
              label: "Publish",
              icon: ArrowRight,
              onClick: () => {
                // TODO: Implement publish functionality
                toast.success("Application published");
              },
            },
            {
              label: "View Details",
              icon: Eye,
              onClick: () => navigate(`/applications/${application.id}`),
            },
            {
              label: "Assign to Customers",
              icon: UserPlus,
              onClick: () => setIsCustomerDialogOpen(true),
            },
            {
              label: "Assign Tags",
              icon: Tags,
              onClick: () => setIsTagAssignmentOpen(true),
            },
            {
              label: "Configure",
              icon: Settings,
              onClick: () => setIsConfigDialogOpen(true),
            },
            {
              label: "Delete",
              icon: Trash2,
              onClick: () => setIsDeleteDialogOpen(true),
              destructive: true,
            },
          ]}
        />
      </GlassCardHeader>
      <GlassCardContent className="p-4 pt-3 space-y-3">
        <div className="text-sm">
          <div className="flex justify-between mb-1">
            <span className="text-muted-foreground">Version</span>
            <span>{application.version}</span>
          </div>
          <div className="flex justify-between mb-1">
            <span className="text-muted-foreground">Size</span>
            <span>{formatAppSize(application.size)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Released</span>
            <span>{application.releaseDate}</span>
          </div>
        </div>
        <div className="pt-2">
          <div className="flex flex-wrap gap-1 mb-2">
            {application.platform && (
              <PlatformIcons platforms={[application.platform as Platform]} size="sm" />
            )}
            {application.source && (() => {
              const meta = getAppSourceMeta(application.source);
              return <Badge variant={meta.variant}>{meta.label}</Badge>;
            })()}
          </div>
          <div className="text-sm">
            <span className="text-muted-foreground block mb-1">Assigned to:</span>
            <div className="flex flex-wrap gap-1 items-center">
              {customerNames.map((name, i) => (
                <CustomerTag key={`c-${i}`} customer={name} />
              ))}
              <TagsDisplay resourceId={application.id} resourceType="application" maxTags={3} showRemoveButton />
            </div>
          </div>
        </div>
      </GlassCardContent>
      
      <Dialog open={isCustomerDialogOpen} onOpenChange={setIsCustomerDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign {application.name}</DialogTitle>
              <DialogDescription>
                Select customers to assign this application to
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <ProfileAssignment 
                form={form} 
                setCustomers={setSelectedCustomers}
                setGroups={setSelectedGroups}
                initialCustomers={application.customers}
                initialGroups={[]}
                multipleCustomers={true}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCustomerDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateCustomers}>
                Save Assignments
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        <ConfigureApplicationDialog
          application={application}
          open={isConfigDialogOpen}
          onOpenChange={setIsConfigDialogOpen}
          onSave={handleSaveConfiguration}
        />

        <TagAssignmentDialog
          open={isTagAssignmentOpen}
          onOpenChange={setIsTagAssignmentOpen}
          resourceId={application.id}
          resourceType="application"
          resourceName={application.name}
          onAssignmentComplete={() => {
            toast.success("Tags assigned successfully");
          }}
        />

        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Application</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{application.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (onRemove) {
                    onRemove(application.id);
                  }
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
    </GlassCard>
  );
};
